<template>
  <div class="settings-page integration">
    <div class="title">
      <div>
        <GmailIcon />
        <h2>Gmail</h2>
      </div>
    </div>
    <p class="description">
      Turn starred emails into tasks and manage them in Routine.
    </p>
    <Login
      v-if="!access_granted && link"
      :uri="link"
      :deeplink="deeplink"
      :scopes="missing_scopes"
      @back="back"
    />
    <p v-else-if="access_granted">
      Access to Gmail is already granted for {{ selected_profile_email }}
    </p>
    <p v-else>Checking status</p>
  </div>
</template>

<script lang="ts">
import * as _ from "lodash-es";
import { computed, defineComponent, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { GoogleScope } from "@ctrl/controller-rpc";

import GmailIcon from "@/components/icons/apps/google/GmailIcon.vue";
import * as inject from "@/components/inject";
import Login from "@/components/Login.vue";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import { firstRouteParam, navigateTo } from "@/utils/navigate";

export default defineComponent({
  components: {
    GmailIcon,
    Login,
  },
  props: {
    orgId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const route = useRoute();
    const deeplink = computed(() => in_electron);
    const selected_profile_email = computed(
      () => firstRouteParam(route.params["id"]) ?? null
    );
    const releaser = useReleaser();
    const identities = await releaser.add(controller.auth.profiles());
    const profile = computed(() =>
      identities.value.find((p) => p.email === selected_profile_email.value)
    );
    const access_granted = computed(() => {
      const p = profile.value;
      return p && p.kind.provider === "google"
        ? p.kind.granted_scopes.gmail || false
        : false;
    });
    const link = ref<string | null>(null);
    const missing_scopes = ref<GoogleScope>();
    const connect = () => {
      const p = profile.value;
      if (!p || p.kind.provider !== "google") return;
      let newScopes = _.clone(p.kind.granted_scopes);
      newScopes.gmail = true;
      controller.auth
        .requestScopes(p.kind.identity, newScopes, window.location.href)
        .then((info) => {
          if (info) {
            link.value = info.grant_uri;
            missing_scopes.value = info.missing_scopes;
          }
        });
    };
    const back = () => {
      navigateTo(RouteNames.SettingsIntegrations, router, {
        params: { orgId: props.orgId },
      });
    };
    const submit = () => {
      if (!selected_profile_email.value) {
        back();
        return;
      }
      navigateTo(RouteNames.SettingsIntegrationsGmail, router, {
        params: { orgId: props.orgId },
        query: {
          activate: selected_profile_email.value,
        },
      });
    };
    releaser.defer(
      watch(access_granted, (granted) => {
        if (granted) {
          submit();
        }
      })
    );
    if (access_granted.value) {
      submit();
    } else {
      connect();
    }
    return {
      access_granted,
      back,
      deeplink,
      link,
      missing_scopes,
      selected_profile_email,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

:deep(.login-container) {
  width: 100%;
}
</style>
